import { AxiosInstance } from 'axios';
import AuthService from '@/lib/auth/auth.service';
import { GenericResponse, Order, OrderProduction, OrderProductionFile } from '@/lib/common/types';
import { PARAMS } from '../../../../params';
import { OrderStatusEnum } from '@/lib/common/types/order-status.enum';

export class DataService {
  private order: Order | undefined;

  constructor(
    private $axios: AxiosInstance,
    private authService: AuthService,
  ) {}

  public async loadData(): Promise<void> {
      if (!this.authService.getOrder()) {
          return;
      }

      const orderDataResult = await this.$axios.post<GenericResponse<Order>>('data/get', null, {
          ...this.authService.signRequest(),
      });
      const { data: orderResult } = orderDataResult;
      if (orderResult?.status) {
          this.order = { ...orderResult.data };
      }
  }

  public getOrderId(): string | undefined {
      return this.order?.order;
  }

  public getOrderStatus(): OrderStatusEnum | undefined {
      return Number(this.order?.orderStatus || 0);
  }

  public getOrderDate(): string | undefined {
      return this.order?.orderDate;
  }

  public isOrderDownloaded(): boolean {
      return !!this.order?.downloaded;
  }

  public getLoadedProduction(): OrderProduction[] {
      return this.order?.prod || [];
  }

  public getPhotomarketVCardId(): string {
      return this.order?.photomarketVcard || '';
  }

  public getPhotomarketJWT(): string {
      return this.order?.orderJwtToken || '';
  }

  public async submitDownloading(): Promise<boolean> {
      try {
          const submitResult = await this.$axios.post<GenericResponse>('data/submit-downloading',  {}, {
              ...this.authService.signRequest(),
          });
          if (submitResult.data.status) {
              this.order = { ...this.order, downloaded: true } as Order;
          }
          return !!submitResult.data.status;
      } catch (e) {
          return false;
      }
  }

  public async savePhotoComment(photoId: string, comment: string): Promise<boolean> {
      try {
          const submitResult = await this.$axios.post<GenericResponse>('data/save-photo-comment',  { photoId, comment }, {
              ...this.authService.signRequest(),
          });

          return !!submitResult.data.status;
      } catch (e) {
          return false;
      }
  }

  public async launchArchiveCreation(): Promise<boolean> {
      const url = `${PARAMS.backend_url}data/archive`;
      try {
          const validationResultRaw = await this.$axios.get(url, {...this.authService.signRequest()});
          const { data } = validationResultRaw;
          return data.status;
      } catch (e) {
          return false;
      }
  }

  public async getArchive(): Promise<void> {
      const url = `${PARAMS.backend_url}data/archive/${this.authService.getToken()}`;
      window.location = url as any;
  }

  public getImages(production: string): OrderProductionFile[] {
      const filesProduction = this.order?.prod.find(val => val.id === production);
      if (!filesProduction?.files?.length) {
          return [];
      }
      return filesProduction.files.map(file => ({
          id: file.id,
          preview: `${PARAMS.backend_url}data/content${file.preview}`,
          original: `${PARAMS.backend_url}data/content${file.original}`,
          name: file.name,
          comment: file.comment,
      }));
  }

  public async checkIfArchiveCreated(): Promise<boolean> {
      const token = this.authService.getToken();
      const url = `${PARAMS.backend_url}data/archive/check/${token}`;
      try {
          const validationResultRaw = await this.$axios.get(url);
          const { data } = validationResultRaw;
          return data.status;
      } catch (e) {
          return false;
      }
  }

  public async checkArchiveCreationProgress(): Promise<boolean> {
      const token = this.authService.getToken();
      const url = `${PARAMS.backend_url}data/archive/check-progress/${token}`;
      try {
          const validationResultRaw = await this.$axios.get(url);
          const { data } = validationResultRaw;
          return data.status;
      } catch (e) {
          return false;
      }
  }
}
